import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";

const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "840px",
  margin: "0 auto",
  padding: "1rem",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
  [theme.breakpoints.up("sm")]: {
    padding: "2rem 1.5rem",
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: "1.5rem",
  color: "#102138",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "rgba(16, 33, 56, 0.08)",
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  color: "#102138",
  fontWeight: 700,
  fontSize: "1.5rem",
  marginBottom: "1.5rem",
  paddingBottom: "0.75rem",
  borderBottom: "2px solid #d4a017",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.75rem",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "#2c3e50",
  fontWeight: 600,
  fontSize: "1.15rem",
  marginTop: "1.75rem",
  marginBottom: "0.5rem",
}));

const BodyText = styled(Typography)(({ theme }) => ({
  color: "#4a5568",
  fontSize: "1rem",
  lineHeight: 1.75,
  marginBottom: "1rem",
}));

const Subtext = styled(Typography)(({ theme }) => ({
  color: "#718096",
  fontSize: "0.875rem",
  marginTop: "1.5rem",
  fontStyle: "italic",
}));

const BulletList = styled("ul")(({ theme }) => ({
  color: "#4a5568",
  fontSize: "1rem",
  lineHeight: 1.75,
  marginBottom: "1rem",
  paddingLeft: "1.5rem",
  "& li": { marginBottom: "0.5rem" },
}));

export const AboutPage = ({ onBack }) => (
  <PageContainer>
    <BackButton onClick={onBack} startIcon={<span>←</span>}>
      Back to Home
    </BackButton>
    <PageTitle variant="h1">About SimplyStock</PageTitle>

    <BodyText>
      SimplyStock is an investment research platform that empowers individual
      investors with fundamental analysis, screening tools, and market data.
      We provide objective financial information to help you make informed
      investment decisions—faster, easier, and simpler.
    </BodyText>

    <SectionTitle>Our Mission</SectionTitle>
    <BodyText>
      We believe every investor deserves access to institutional-grade research
      tools. Our mission is to demystify the stock market by delivering
      clear, accurate financial data and analysis in a format that's easy to
      understand and act on.
    </BodyText>

    <SectionTitle>What We Offer</SectionTitle>
    <BulletList>
      <li><strong>Fundamental Analysis</strong> — Company profiles, financial metrics, valuation models, and intrinsic value estimates</li>
      <li><strong>Stock Screeners</strong> — Filter thousands of stocks by growth, value, quality, and custom criteria</li>
      <li><strong>Portfolio Tools</strong> — Track performance, analyze holdings, and monitor your investments</li>
      <li><strong>Market Data</strong> — Real-time quotes, price history, earnings calendars, and key financial ratios</li>
      <li><strong>Curated Ideas</strong> — Investment themes, sector analysis, and research-backed stock lists</li>
    </BulletList>

    <SectionTitle>Our Approach</SectionTitle>
    <BodyText>
      SimplyStock maintains editorial independence. We do not provide
      personalized investment advice, buy/sell recommendations, or act as a
      broker-dealer. Our analysis is based on publicly available data and
      standardized methodologies. Users are encouraged to conduct their own
      due diligence before making any investment decisions.
    </BodyText>

    <SectionTitle>Data Sources</SectionTitle>
    <BodyText>
      Our financial data is sourced from established third-party providers
      and regulatory filings. We strive to present accurate, timely
      information, but we recommend verifying critical data with primary
      sources such as SEC filings and company reports.
    </BodyText>

    <SectionTitle>Contact</SectionTitle>
    <BodyText>
      For questions about SimplyStock, please contact us at{" "}
      <strong>info@SimplyStock.net</strong>.
    </BodyText>
  </PageContainer>
);

const TERMS_HTML = `
<p style="font-weight: 600; color: #2c3e50; margin-bottom: 1rem;">Last Updated: February 2026</p>

<h4>1. Investment Disclaimer</h4>
<p>SimplyStock.net is a financial information and research platform. We are NOT a registered broker-dealer, investment adviser, or financial planner. The content on this website—including stock analysis, valuations, screeners, and all other data—is provided for informational and educational purposes only. Nothing on this site constitutes a recommendation to buy, sell, or hold any security. Past performance does not guarantee future results. You are solely responsible for your investment decisions.</p>

<h4>2. No Personalized Advice</h4>
<p>We do not provide personalized investment advice tailored to your financial situation, risk tolerance, or goals. All analysis, scores, and metrics are based on general methodologies and publicly available data. Consult a qualified financial professional for advice specific to your circumstances.</p>

<h4>3. Accuracy and "As Is" Basis</h4>
<p>While we strive for accuracy, SimplyStock does not guarantee the completeness, correctness, or timeliness of any content. Financial data is sourced from third parties and may contain errors or lag. Data refresh schedules vary (e.g., quarterly for fundamentals). The website and all content are provided "as is" without warranties of any kind. You assume all risk of using this information.</p>

<h4>4. Limitation of Liability</h4>
<p>To the fullest extent permitted by law, SimplyStock and its affiliates shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of this website or reliance on its content. This includes, but is not limited to, trading losses, lost profits, or data inaccuracies.</p>

<h4>5. Intellectual Property</h4>
<p>All content on SimplyStock.net—including text, graphics, logos, methodologies, and software—is owned by SimplyStock or its licensors. You may not reproduce, distribute, modify, or create derivative works without our prior written consent. Unauthorized use may violate copyright and other laws.</p>

<h4>6. Stock Coverage and Data</h4>
<p>We cover a curated universe of U.S. equities. Coverage does not include all publicly traded companies, newly listed IPOs, or mutual funds. Data may differ from other sources due to timing, methodology, or provider differences. Always verify material data with primary sources.</p>

<h4>7. Account and Access</h4>
<p>We reserve the right to suspend, restrict, or terminate your access at any time, without notice, for violation of these Terms or for any other reason at our discretion.</p>

<h4>8. Indemnification</h4>
<p>By using this website, you agree to indemnify and hold harmless SimplyStock, its affiliates, and their respective officers, directors, and employees from any claims, damages, or expenses arising from your use of the site or violation of these Terms.</p>

<h4>9. Modifications</h4>
<p>We may update these Terms at any time. Continued use of the website after changes constitutes acceptance of the revised Terms. We encourage you to review this page periodically.</p>

<h4>10. Governing Law</h4>
<p>These Terms are governed by the laws of the United States. Any disputes shall be resolved in accordance with applicable law.</p>
`;

export const TermsPage = ({ onBack }) => (
  <PageContainer>
    <BackButton onClick={onBack} startIcon={<span>←</span>}>
      Back to Home
    </BackButton>
    <PageTitle variant="h1">Terms of Service</PageTitle>
    <Box
      sx={{
        "& h4": { color: "#2c3e50", fontWeight: 600, fontSize: "1.1rem", marginTop: "1.5rem", marginBottom: "0.35rem" },
        "& p": { color: "#4a5568", fontSize: "1rem", lineHeight: 1.75, marginBottom: "0.85rem" },
      }}
      dangerouslySetInnerHTML={{ __html: TERMS_HTML }}
    />
  </PageContainer>
);

export const PrivacyPage = ({ onBack }) => (
  <PageContainer>
    <BackButton onClick={onBack} startIcon={<span>←</span>}>
      Back to Home
    </BackButton>
    <PageTitle variant="h1">Privacy Policy</PageTitle>

    <Subtext>Last Updated: February 2026</Subtext>

    <BodyText>
      SimplyStock ("we," "us," or "our") is committed to protecting your
      privacy. This Privacy Policy describes how we collect, use, disclose,
      and safeguard your information when you use our website and services.
      By using SimplyStock.net, you consent to the practices described
      herein.
    </BodyText>

    <SectionTitle>Information We Collect</SectionTitle>
    <BodyText>
      <strong>Information you provide:</strong> When you register or contact
      us, we may collect your name, email address, and any other information
      you voluntarily submit.
    </BodyText>
    <BodyText>
      <strong>Automatically collected information:</strong> When you visit
      our site, we may collect your IP address, browser type, device
      information, pages visited, and usage patterns. We use cookies and
      similar technologies to improve functionality and analyze site
      performance.
    </BodyText>

    <SectionTitle>How We Use Your Information</SectionTitle>
    <BodyText>
      We use your information to: provide and improve our services; personalize
      your experience; communicate with you about your account, updates, and
      relevant content; analyze usage to enhance our platform; prevent fraud and
      ensure security; and comply with legal obligations. We do not sell your
      personal information to third parties.
    </BodyText>

    <SectionTitle>Information Sharing</SectionTitle>
    <BodyText>
      We may share information with service providers who assist in operating
      our website (e.g., hosting, analytics) under strict confidentiality
      agreements. We may disclose information when required by law, to protect
      our rights, or in connection with a merger or acquisition.
    </BodyText>

    <SectionTitle>Data Security</SectionTitle>
    <BodyText>
      We implement industry-standard security measures—including encryption,
      access controls, and secure protocols—to protect your information from
      unauthorized access, alteration, or destruction. However, no method of
      transmission over the Internet is 100% secure.
    </BodyText>

    <SectionTitle>Your Rights</SectionTitle>
    <BodyText>
      Depending on your location, you may have rights to access, correct,
      delete, or restrict processing of your personal data. You may also
      opt out of marketing communications. To exercise these rights, contact
      us at info@SimplyStock.net.
    </BodyText>

    <SectionTitle>Cookies</SectionTitle>
    <BodyText>
      We use cookies and similar technologies for authentication, preferences,
      and analytics. You can manage cookie settings through your browser;
      disabling certain cookies may affect site functionality.
    </BodyText>

    <SectionTitle>Changes to This Policy</SectionTitle>
    <BodyText>
      We may update this Privacy Policy from time to time. We will post the
      revised policy on this page and update the "Last Updated" date.
      Continued use of our services after changes constitutes acceptance of
      the updated policy.
    </BodyText>

    <SectionTitle>Contact Us</SectionTitle>
    <BodyText>
      For privacy-related questions or to exercise your rights, contact us
      at <strong>info@SimplyStock.net</strong>.
    </BodyText>
  </PageContainer>
);
